import { FC, useEffect, useMemo } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import { Box, Button, Divider, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  adminPostNotificationReqDtoSchema,
  useAdminNotificationControllerCreate,
  useAdminNotificationNotificationRecipientControllerCreate,
} from "service";
import { AdminPostNotificationReqDto } from "service/api/estabrookSchemas";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";
import { usePendingRecipients } from "../../../hooks/use-pending-recipients";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import NotificationForm from "./notification-form";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import Navbar from "../../../components/navbar/navbar";

const AddNotificationPage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const preSelectedRecipients = useMemo(() => {
    const recipients = searchParams.get("recipients");
    return recipients ? recipients.split(",").filter(Boolean) : [];
  }, [searchParams]);

  const {
    pendingRecipients,
    setPendingRecipients,
    clearRecipients,
    hasRecipients,
  } = usePendingRecipients(preSelectedRecipients, false);

  useEffect(() => {
    if (preSelectedRecipients.length > 0) {
      setPendingRecipients(preSelectedRecipients);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("recipients");
      const newUrl = newParams.toString() ? `?${newParams.toString()}` : "";
      navigate(`${AdRoutes.addNotification}${newUrl}`, { replace: true });
    }
  }, [preSelectedRecipients, searchParams, navigate, setPendingRecipients]);

  const { mutate: addRecipient } =
    useAdminNotificationNotificationRecipientControllerCreate();

  const { mutate: createNotification, isLoading } =
    useAdminNotificationControllerCreate({
      onSuccess: async (data) => {
        const notificationId = data.data?.id;
        if (!notificationId) {
          toast.error("Failed to create notification");
          return;
        }

        if (hasRecipients) {
          // Add recipients with proper callbacks
          addRecipient(
            {
              pathParams: { notificationId: notificationId },
              body: {
                recipients: pendingRecipients.map((recipient) => {
                  // Parse recipient format: "customerId:locationId" or just "customerId"
                  const parts = recipient.split(":");
                  if (parts.length === 2) {
                    // New format with location ID
                    return {
                      customerId: parts[0],
                      locationId: parts[1],
                    };
                  } else {
                    // Legacy format without location ID
                    return {
                      customerId: recipient,
                      locationId: "",
                    };
                  }
                }),
              },
            },
            {
              onSuccess: () => {
                toast.success(
                  "Notification created with recipients successfully!",
                );
                clearRecipients();
                resetForm();
                setTimeout(() => {
                  navigate(
                    AdRoutes.notificationRecipients.replace(
                      ":notificationId",
                      notificationId,
                    ),
                  );
                }, 10);
              },
              onError: (error) => {
                console.error("Failed to add recipients:", error);
                toast.error(
                  "Notification created but failed to add recipients",
                );
                clearRecipients();
                resetForm();
                setTimeout(() => {
                  navigate(
                    AdRoutes.notificationRecipients.replace(
                      ":notificationId",
                      notificationId,
                    ),
                  );
                }, 10);
              },
            },
          );
        } else {
          toast.success("Notification Created Successfully");
          clearRecipients();
          resetForm();
          setTimeout(() => {
            navigate(
              AdRoutes.notificationRecipients.replace(
                ":notificationId",
                notificationId,
              ),
            );
          }, 10);
        }
      },
    });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = useFormik<AdminPostNotificationReqDto>({
    initialValues: {
      title: "",
      description: "",
      type: ["push"],
      deliveryType: "instant",
    },
    validationSchema: toFormikValidationSchema(
      adminPostNotificationReqDtoSchema,
    ),
    validate: toFormikValidate(adminPostNotificationReqDtoSchema),
    onSubmit: () => {
      createNotification({ body: { ...values } });
    },
  });

  useUnsavedChangesBlocker(dirty);

  const handleManageRecipients = () => {
    submitForm();
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Notification Center",
                url: AdRoutes.notifications,
              },
              {
                label: "Create Notification",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              data-testid="save-notification-button"
            >
              Save Notification
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <NotificationForm
        values={values}
        handleChange={handleChange}
        handleBlur={handleBlur}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
      />
      <Divider sx={{ mt: "40px" }} />
      <Box sx={styles.customerDetailContainer}>
        <Typography fontWeight={600}>Recipients</Typography>
        {hasRecipients ? (
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {pendingRecipients.length} recipient
              {pendingRecipients.length > 1 ? "s" : ""} ready to be added
            </Typography>
            <Button
              variant="outlined"
              onClick={handleManageRecipients}
              data-testid="manage-recipients-button"
            >
              Manage Recipients
            </Button>
          </Box>
        ) : (
          <Button
            disabled={isLoading || !isValid}
            variant="outlined"
            onClick={handleManageRecipients}
            data-testid="add-recipients-button"
          >
            Add Recipients
          </Button>
        )}
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
}));

export default AddNotificationPage;
